import React from "react";
import { Link, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import {
  IoCompassOutline,
  IoChevronBack,
  IoChevronForward,
  IoHelpCircleOutline,
} from "react-icons/io5";
import Flex from "./patterns/Flex";
import Box from "./patterns/Box";
import Text from "./patterns/Text";
import ButtonPill from "./patterns/ButtonPill";
import UserPhoto from "./patterns/UserPhoto";
import { getUserPhotoPlaceholderString } from "./messages/MessageRow";
import CreateStoryButton from "./story/CreateStoryButton";
import ModalOpener from "./modal/manager/ModalOpener";
import { ModalRegistry } from "./modal/utils";
import logo from "../../src/assets/logo.svg";
import { db, storage } from "../utils/firebase";

export const SIDEBAR_EXPANDED_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

const STORAGE_KEY = "sidebarCollapsed";

const NavSidebar = () => {
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const location = useLocation();
  const auth = getAuth();
  const user = auth.currentUser;
  const isLoggedIn = user !== null;

  const [imgURL, setImgURL] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (auth.currentUser?.uid) {
      const profilePhotoRef = ref(
        storage,
        `profilePhotos/${auth.currentUser?.uid}`
      );
      getDownloadURL(profilePhotoRef)
        .then((url) => setImgURL(url))
        .catch(() => {});
    }
  }, [auth.currentUser?.uid]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {}
  };

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const isActive = (path: string) => location.pathname === path;

  const navItemStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    padding: collapsed ? "10px 0" : "10px 16px",
    justifyContent: collapsed ? "center" : "flex-start",
    cursor: "pointer",
    borderRadius: 8,
    background: active ? "rgba(13, 110, 253, 0.08)" : undefined,
    color: active ? "#0D6EFD" : "#383838",
    textDecoration: "none",
    marginBottom: 4,
    transition: "background 0.15s",
  });

  return (
    <Flex
      style={{
        width,
        minWidth: width,
        height: "100vh",
        flexDirection: "column",
        borderRight: "1px solid lightgray",
        background: "#f9f9f9",
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflow: "hidden",
      }}
    >
      {/* Logo */}
      <Flex
        style={{
          padding: collapsed ? "16px 0" : "16px 16px",
          justifyContent: collapsed ? "center" : "flex-start",
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Link to="/">
          <img
            src={logo}
            style={{ height: 28 }}
            alt="Tiny Fiction"
          />
        </Link>
      </Flex>

      {/* Navigation items */}
      <Box
        style={{
          padding: collapsed ? "12px 8px" : "12px 12px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Discover */}
        <Link to="/discover" style={navItemStyle(isActive("/discover") || isActive("/"))}>
          <IoCompassOutline size={22} />
          {!collapsed && (
            <Text
              style={{
                marginLeft: 10,
                fontSize: 15,
                fontWeight: isActive("/discover") || isActive("/") ? 600 : 500,
                color: "inherit",
              }}
            >
              Discover
            </Text>
          )}
        </Link>

        {/* Create story */}
        <Box style={{ marginTop: 4 }}>
          <CreateStoryButton
            style={{
              width: collapsed ? 40 : "100%",
              height: 36,
              fontSize: 14,
              padding: collapsed ? 0 : undefined,
            }}
          >
            {collapsed ? "+" : "Create story"}
          </CreateStoryButton>
        </Box>

        {/* Spacer */}
        <Box style={{ flex: 1 }} />

        {/* Send feedback */}
        <ModalOpener
          modal={ModalRegistry.textInputModal}
          modalProps={{
            placeholder:
              "Give feedback, ask a question, or make a suggestion to the team",
            title: "Send us feedback",
            textLimit: 250,
            didSubmit: (text: string) => {
              const loggedInUserId = getAuth().currentUser?.uid;
              const currentTime = Date.now();

              if (loggedInUserId) {
                const mesRef = doc(db, "userFeedback", loggedInUserId);
                setDoc(
                  mesRef,
                  { [currentTime]: { note: text } },
                  { merge: true }
                );
              } else {
                const date = Date.now().toString();
                const mesRef = doc(db, "userFeedback", date);
                setDoc(mesRef, { note: text }, { merge: true });
              }
            },
            useMinHeight: true,
          }}
        >
          {(feedbackModal) => (
            <Flex
              style={{
                ...navItemStyle(false),
                marginBottom: 8,
              }}
              onClick={() => feedbackModal.toggleModal(true)}
            >
              <IoHelpCircleOutline size={22} />
              {!collapsed && (
                <Text
                  style={{
                    marginLeft: 10,
                    fontSize: 14,
                    fontWeight: 500,
                    color: "inherit",
                  }}
                >
                  Send feedback
                </Text>
              )}
            </Flex>
          )}
        </ModalOpener>

        {/* User profile / Sign up */}
        {isLoggedIn ? (
          <Link
            to={`/profile/${user?.uid}`}
            style={{
              ...navItemStyle(false),
              marginBottom: 8,
              textDecoration: "none",
            }}
          >
            <UserPhoto
              size={28}
              URL={imgURL}
              userId={user?.uid}
              placeholderText={
                user?.displayName
                  ? getUserPhotoPlaceholderString(user.displayName)
                  : undefined
              }
            />
            {!collapsed && (
              <Text
                style={{
                  marginLeft: 10,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#383838",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.displayName || "Profile"}
              </Text>
            )}
          </Link>
        ) : (
          <ModalOpener
            modal={ModalRegistry.signupModal}
            modalProps={{ signUp: true }}
          >
            {(options) => (
              <Box style={{ marginBottom: 8 }}>
                <ButtonPill
                  size="sm"
                  variant="outline-secondary"
                  style={{
                    width: collapsed ? 40 : "100%",
                    fontSize: 13,
                  }}
                  onClick={() => options.toggleModal(true)}
                >
                  {collapsed ? "?" : "Sign up"}
                </ButtonPill>
              </Box>
            )}
          </ModalOpener>
        )}

        {/* Collapse toggle */}
        <Flex
          style={{
            ...navItemStyle(false),
            marginBottom: 0,
          }}
          onClick={toggleCollapse}
        >
          {collapsed ? (
            <IoChevronForward size={20} color="gray" />
          ) : (
            <>
              <IoChevronBack size={20} color="gray" />
              <Text
                style={{
                  marginLeft: 10,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "gray",
                }}
              >
                Collapse
              </Text>
            </>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

export default NavSidebar;
